import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/atoms/button';
import { ROUTES } from '@constants';
import { useToast } from '@hooks/use-toast';
import { useLazyGetVerificationByShortIdQuery } from '@modules/dashboard/api/verifications-api';
import type { RootState } from '@store/root-reducer';

export const PreviewButton: React.FC = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const organizationId = useSelector(
        (state: RootState) => state.dashboard.session.organizationId,
    );
    const id = useSelector((state: RootState) => state.docsVerification.sdkOptions?.id);
    const [getVerificationByShortId, { isLoading }] = useLazyGetVerificationByShortIdQuery();
    const errorMessage = useMemo(() => t('common.pleaseTryAgainLater'), [t]);
    const onVerificationPreviewClick = useCallback(async () => {
        try {
            if (!id) {
                return;
            }
            const response = await getVerificationByShortId({
                organizationId,
                id,
            }).unwrap();

            navigate(`${ROUTES.KYC.DOCVER.RESULT}${response.id}`);
        } catch (e) {
            showToast({
                variant: 'error',
                title: t('common.somethingWentWrong'),
                message: errorMessage,
            });
        }
    }, [id, getVerificationByShortId, organizationId, navigate, showToast, t, errorMessage]);

    return (
        <Button
            variant='tertiary'
            onClick={onVerificationPreviewClick}
            className='button-redirect'
            size='xl'
            loading={isLoading}
        >
            {t('docVerificationWidget.finish.checkVerificationDetails')}
        </Button>
    );
};
